import { UserRole } from '../../types';
import type { RoleMatrix, PermissionResource, PermissionAction } from './types';

// =================================================================
//                        ACTION DEFINITIONS
// =================================================================
// Actions are grouped by type for better readability and management.

const READ_ACTIONS: PermissionAction[] = ['view', 'list', 'export', 'download'];
const MUTATE_ACTIONS: PermissionAction[] = ['create', 'edit', 'update', 'delete', 'archive', 'reopen'];
const WORKFLOW_ACTIONS: PermissionAction[] = [
    'assign',
    'unassign',
    'send',
    'submit',
    'approve',
    'reject',
    'validate',
    'publish',
    'close',
    'escalate',
    'comment',
    'confirm',
    'cancel',
    'withdraw',
    'notify',
    'remind',
    // Gate control of media badges (media_access_log) — see SECURITY_GATE.
    'scan',
];
const FINANCIAL_ACTIONS: PermissionAction[] = [
    'submit_accounting',
    'validate_accounting',
    'reject_accounting',
    'close_accounting_period',
    'reopen_accounting_period',
    'transfer',
    'calculate',
];
const ADMIN_ACTIONS: PermissionAction[] = ['manage', 'edit_role', 'impersonate', 'optimize', 'sync', 'bulk_action'];

const ALL_ACTIONS: PermissionAction[] = [
    ...READ_ACTIONS,
    ...MUTATE_ACTIONS,
    ...WORKFLOW_ACTIONS,
    ...FINANCIAL_ACTIONS,
    ...ADMIN_ACTIONS,
];

// =================================================================
//                       RESOURCE DEFINITIONS
// =================================================================
// Resources are grouped by domain for improved clarity in role definitions.

const PLANNING_RESOURCES: PermissionResource[] = [
    'dashboard',
    'league',
    'league_group',
    'match',
    'matches',
    'match_sheet',
    'assignment',
    'assignments',
    'assignment_proposal',
    'mission',
    'missions',
    'calendar',
    'calendar_config',
];
const RANKINGS_RESOURCES: PermissionResource[] = [
    'ranking',
    'rankings',
];
const ENTITY_RESOURCES: PermissionResource[] = [
    'user',
    'users',
    'official',
    'officials',
    'club',
    'clubs',
    'venue',
    'venues',
    'player',
    'players',
    'stadium',
    'stadiums',
    'staff',
    'team',
    'teams',
    'season',
    'seasons',
    'availability',
    'availabilities',
];
const FINANCE_RESOURCES: PermissionResource[] = [
    'finance_dashboard',
    'expense_report',
    'payment',
    'accounting_entry',
    'accounting_period',
    'accounting_reports',
];
const DISCIPLINARY_RESOURCES: PermissionResource[] = [
    'disciplinary',
    'disciplinary_case',
    'disciplinary_session',
    'suspension',
    'suspensions',
    'sanction',
    'sanctions',
    'disciplinary_accumulation_rule',
    'disciplinary_reason',
];
const SYSTEM_RESOURCES: PermissionResource[] = [
    'audit_log',
    'setting',
    'settings',
    'document',
    'documents',
    'import',
    'imports',
    'import_job',
    'export_job',
    'stadium_audit',
    'league_role',
];
const NEWS_RESOURCES: PermissionResource[] = [
    'news',
    'news_article',
];
// Media accreditation. 'media' gates the module (route + nav); the others gate
// the individual review queues inside it.
const MEDIA_RESOURCES: PermissionResource[] = [
    'media',
    'media_organization',
    'media_personnel',
    'media_league_access',
    'media_accreditation',
    'media_access_log',
    'media_badge_template',
];
const COMMUNICATION_RESOURCES: PermissionResource[] = [
    'communication',
    'communications',
    'email',
    'emails',
    'notification',
    'notifications',
];

const ALL_RESOURCES: PermissionResource[] = [
    ...PLANNING_RESOURCES,
    ...RANKINGS_RESOURCES,
    ...ENTITY_RESOURCES,
    ...FINANCE_RESOURCES,
    ...DISCIPLINARY_RESOURCES,
    ...NEWS_RESOURCES,
    ...MEDIA_RESOURCES,
    ...SYSTEM_RESOURCES,
    ...COMMUNICATION_RESOURCES,
];

// =================================================================
//                           ROLE MATRIX
// =================================================================

export const ROLE_MATRIX: RoleMatrix = {
    // --- System & Global Roles ---

    [UserRole.SUPER_ADMIN]: {
        key: UserRole.SUPER_ADMIN,
        label: 'Super Administrateur',
        description: 'Accès total au système. Gère les configurations critiques, les administrateurs et la sécurité.',
        grants: [
            {
                resources: ['*'], // Access to all current and future resources
                actions: ['*'],   // Access to all current and future actions
                scope: 'global',
            },
        ],
    },
    [UserRole.ADMIN]: {
        key: UserRole.ADMIN,
        label: 'Administrateur Fédéral',
        description: "Gère l'ensemble de la plateforme pour la fédération. Configure les données maîtres et les utilisateurs.",
        inherits: [UserRole.PLANNING_MANAGER, UserRole.ACCOUNTING_MANAGER, UserRole.DISCIPLINARY_ADVOCATE],
        grants: [
            {
                // Explicitly grants full control over master data entities, overriding read-only inherited permissions.
                resources: ['official', 'officials', 'club', 'clubs', 'venue', 'venues', 'team', 'teams', 'player', 'players', 'stadium', 'stadiums', 'staff', 'season', 'seasons', 'league', 'league_group'],
                actions: [...READ_ACTIONS, ...MUTATE_ACTIONS],
                scope: 'global',
            },
            {
                // High-level permissions for user, settings, and data import management.
                resources: ['users', 'settings', 'imports', 'import_job', 'export_job', 'league_role'],
                actions: ['manage', 'edit_role', ...READ_ACTIONS, ...MUTATE_ACTIONS, 'optimize', 'sync', 'bulk_action'],
                scope: 'global',
            },
            {
                // Full news management (create, edit, publish, delete articles).
                resources: ['news', 'news_article'],
                actions: [...READ_ACTIONS, ...MUTATE_ACTIONS, 'publish'],
                scope: 'global',
            },
        ],
    },
    [UserRole.LEAGUE_PRESIDENT]: {
        key: UserRole.LEAGUE_PRESIDENT,
        label: 'Président de Ligue',
        description: 'Supervise la gestion complète (planning, discipline, finances) des ligues qui lui sont assignées.',
        // A League President has the combined power of all managers within their scope.
        inherits: [UserRole.PLANNING_MANAGER, UserRole.ACCOUNTING_MANAGER, UserRole.DISCIPLINARY_ADVOCATE, UserRole.COORDINATOR],
        grants: [
            {
                // High-level permissions for user, settings, and data import management.
                resources: ['users', 'settings', 'league_role'],
                actions: ['manage', 'edit_role', ...READ_ACTIONS, ...MUTATE_ACTIONS, 'optimize', 'sync', 'bulk_action'],
                scope: 'assigned-leagues',
            },
            {
                // Stadium audit planning relies on missions (assignment + orders) and audit visibility.
                // 'validate' covers reviewing/approving audit submissions returned by officials.
                // 'create'/'update'/'delete' allow full lifecycle management (assign, change, remove officials, delete audits).
                resources: ['mission', 'missions', 'stadium_audit'],
                actions: [...READ_ACTIONS, 'create', 'update', 'delete', 'assign', 'unassign', 'send', 'validate'],
                scope: 'assigned-leagues',
            },
            {
                resources: ['rankings', 'ranking'],
                actions: [...READ_ACTIONS, 'manage', 'create'],
                scope: 'assigned-leagues',
            },
            {
                // News management scoped to assigned leagues.
                resources: ['news', 'news_article'],
                actions: [...READ_ACTIONS, ...MUTATE_ACTIONS, 'publish'],
                scope: 'assigned-leagues',
            },
            {
                // Override: allows editing assignments on completed matches (bypasses scheduled-only restriction).
                resources: ['assignment', 'assignments'],
                actions: ['manage'],
                scope: 'assigned-leagues',
            },
            {
                // Media accreditation: the league president owns every review queue.
                // This replaces the hardcoded `role === SUPER_ADMIN || LEAGUE_PRESIDENT`
                // gates that used to guard the /media route, its nav item and the
                // notification center. Grants are per-queue so a future
                // media_manager role can be given a strict subset.
                //
                // No 'create': organizations and journalists are submitted from
                // the mobile app and the league only reviews them. The RLS
                // policies on the shared Supabase project enforce the same
                // thing, so granting create here would produce a button that
                // always fails.
                //
                // 'delete' = the league's final removal of a journalist or an
                // organisation (with all its journalists) through the
                // `delete-media-account` edge function — never a direct DELETE.
                // The server re-checks it: the caller must be president/admin
                // of every league the account is tied to.
                resources: ['media_organization', 'media_personnel'],
                actions: [...READ_ACTIONS, 'edit', 'update', 'approve', 'reject', 'delete'],
                scope: 'assigned-leagues',
            },
            {
                // Match accreditations add 'cancel' (revoking one already
                // granted) and 'send' (mailing the badge). Neither is meaningful
                // on an organization or a journalist account, so they get their
                // own grant rather than a widened shared one.
                resources: ['media_accreditation'],
                actions: [...READ_ACTIONS, 'edit', 'update', 'approve', 'reject', 'cancel', 'send'],
                scope: 'assigned-leagues',
            },
            {
                // An organization's access to one league: grant (approve),
                // reject, revoke (cancel). No 'create' — the organization asks
                // from the mobile app — and no edit/send: there is nothing else.
                resources: ['media_league_access'],
                actions: [...READ_ACTIONS, 'approve', 'reject', 'cancel'],
                scope: 'assigned-leagues',
            },
            {
                // Access logs are scanner output — read and export only, never
                // editable: they are the audit trail for who entered the stadium.
                resources: ['media_access_log'],
                actions: [...READ_ACTIONS],
                scope: 'assigned-leagues',
            },
            {
                // 'media' is the module gate only (route + nav + notification
                // center), hence 'view' alone — same shape as 'dashboard'.
                resources: ['media'],
                actions: ['view'],
                scope: 'assigned-leagues',
            },
            {
                // Badge templates are the one media record the league authors
                // itself, hence full CRUD. No export/download: a template is
                // edited in the designer, not shipped as a file.
                resources: ['media_badge_template'],
                actions: ['view', 'list', 'create', 'edit', 'update', 'delete'],
                scope: 'assigned-leagues',
            },
        ],

    },

    // --- Planning & Operations Roles (Improved Separation of Concerns) ---

    [UserRole.PLANNING_MANAGER]: {
        key: UserRole.PLANNING_MANAGER,
        label: 'Responsable Planification',
        description: 'Gère les calendriers, matchs et désignations. Consulte les officiels et clubs pour planifier.',
        grants: [
            {
                // Full control over planning and assignment workflows.
                resources: ['dashboard', 'match', 'matches', 'assignment', 'assignments', 'match_sheet', 'calendar', 'calendar_config'],
                actions: [...READ_ACTIONS, ...MUTATE_ACTIONS, 'assign', 'unassign', 'send', 'notify', 'optimize', 'validate'],
                scope: 'assigned-leagues',
            },
            {
                // Propositions d'arbitres du Responsable Arbitres : la ligue les valide
                // (en écrivant le créneau) ou les refuse. Le président en hérite.
                resources: ['assignment_proposal'],
                actions: [...READ_ACTIONS, 'validate', 'reject'],
                scope: 'assigned-leagues',
            },
            {
                // Full control over stadiums for planning purposes (scheduling matches at venues).
                resources: ['stadium', 'stadiums'],
                actions: [...READ_ACTIONS, ...MUTATE_ACTIONS, 'manage'],
                scope: 'assigned-leagues',
            },
            {
                // Planning owns the stadium audit phase: plan, assign, send orders, and validate submissions.
                // 'create'/'update'/'delete' allow full lifecycle management (assign, change, remove officials, delete audits).
                resources: ['mission', 'missions', 'stadium_audit'],
                actions: [...READ_ACTIONS, 'create', 'update', 'delete', 'assign', 'unassign', 'send', 'validate'],
                scope: 'assigned-leagues',
            },
            {
                // **Read-only** access to entities. The manager finds and assigns officials/clubs/teams, but does not manage their data.
                resources: ['official', 'officials', 'club', 'clubs', 'venue', 'venues', 'team', 'teams', 'player', 'players'],
                actions: READ_ACTIONS,
                scope: 'assigned-leagues',
            },
            { resources: ['rankings', 'ranking'], actions: READ_ACTIONS, scope: 'assigned-leagues' },
            { resources: ['news', 'news_article'], actions: READ_ACTIONS, scope: 'assigned-leagues' },
        ],
    },
    [UserRole.COORDINATOR]: {
        key: UserRole.COORDINATOR,
        label: 'Coordinateur / Secrétaire',
        description: "Responsable des données. Gère les informations des clubs, lieux, équipes, joueurs, staff et officiels pour les ligues assignées.",
        grants: [
            {
                // Empowered to manage the master data for officials, clubs, venues, teams, players, stadiums, and staff within their scope.
                resources: ['official', 'officials', 'club', 'clubs', 'venue', 'venues', 'team', 'teams', 'player', 'players', 'stadium', 'stadiums', 'staff'],
                actions: [...READ_ACTIONS, 'create', 'update', 'comment', 'archive', 'edit', 'manage', 'bulk_action'],
                scope: 'assigned-leagues',
            },
            {
                // Can view the schedule to support planning efforts.
                resources: ['dashboard', 'match', 'matches'],
                actions: READ_ACTIONS,
                scope: 'assigned-leagues',
            },
            {
                // As the data owner for stadiums, the coordinator can also run the audit phase
                // (plan/assign/send/validate) since audits feed back into stadium master data.
                // 'create'/'update'/'delete' allow full lifecycle management (assign, change, remove officials, delete audits).
                resources: ['mission', 'missions', 'stadium_audit'],
                actions: [...READ_ACTIONS, 'create', 'update', 'delete', 'assign', 'unassign', 'send', 'validate'],
                scope: 'assigned-leagues',
            },
        ],
    },

    // --- Accounting Roles (Excellent Separation of Duties) ---

    [UserRole.ACCOUNTING_MANAGER]: {
        key: UserRole.ACCOUNTING_MANAGER,
        label: 'Responsable Comptable',
        description: 'Supervise l’ensemble du cycle comptable, gère les tableaux de bord et les rapports financiers.',
        inherits: [UserRole.ACCOUNTANT_ENTRY, UserRole.ACCOUNTANT_VALIDATOR, UserRole.ACCOUNTANT_CLOSER],
        grants: [
            {
                // Generic accounting and finance resources for menu visibility and high-level access
                resources: ['accounting', 'finances', 'finance_dashboard', 'accounting_reports', 'official', 'officials', 'accounting_entry'],
                actions: [...READ_ACTIONS, 'manage', 'submit_accounting', 'validate_accounting', 'reject_accounting', 'close_accounting_period', 'reopen_accounting_period'],
                scope: 'assigned-leagues',
            },
        ],
    },
    [UserRole.ACCOUNTANT_ENTRY]: {
        key: UserRole.ACCOUNTANT_ENTRY,
        label: 'Comptable - Saisie',
        description: 'Saisit les notes de frais et prépare les paiements pour soumission.',
        grants: [
            {
                resources: ['expense_report', 'payment', 'accounting_entry'],
                actions: [...READ_ACTIONS, 'create', 'update', 'submit_accounting'],
                scope: 'assigned-leagues',
            },
        ],
    },
    [UserRole.ACCOUNTANT_VALIDATOR]: {
        key: UserRole.ACCOUNTANT_VALIDATOR,
        label: 'Comptable - Validation',
        description: 'Valide ou rejette les saisies comptables avant le paiement.',
        grants: [
            {
                resources: ['expense_report', 'payment', 'accounting_entry'],
                actions: [...READ_ACTIONS, 'validate_accounting', 'reject_accounting'],
                scope: 'assigned-leagues',
            },
        ],
    },
    [UserRole.ACCOUNTANT_CLOSER]: {
        key: UserRole.ACCOUNTANT_CLOSER,
        label: 'Comptable - Clôture',
        description: 'Clôture les périodes comptables et initie les virements bancaires.',
        grants: [
            {
                resources: ['accounting_period'],
                actions: [...READ_ACTIONS, 'close_accounting_period', 'reopen_accounting_period'],
                scope: 'assigned-leagues',
            },
            {
                resources: ['payment'],
                actions: ['send'], // Specifically allows triggering the bank transfer process
                scope: 'assigned-leagues',
            },
        ],
    },

    // --- Disciplinary Roles ---

    [UserRole.DISCIPLINARY_ADVOCATE]: {
        key: UserRole.DISCIPLINARY_ADVOCATE,
        label: 'Commissaire Disciplinaire',
        description: 'Instruit les dossiers disciplinaires, ajoute des documents et propose des sanctions.',
        grants: [
            {
                resources: ['disciplinary', 'disciplinary_case', 'disciplinary_session', 'sanction', 'sanctions', 'suspension', 'suspensions', 'disciplinary_accumulation_rule', 'disciplinary_reason', ...SYSTEM_RESOURCES.filter(r => r.startsWith('document'))],
                // Full PV (procès-verbal) lifecycle: build sessions/cases/sanctions,
                // then publish/send/notify. Mirrors the disciplinary_manage_* RLS
                // policies (league_president + disciplinary_advocate get ALL).
                // Inherited by LEAGUE_PRESIDENT.
                actions: [...READ_ACTIONS, 'create', 'edit', 'update', 'comment', 'calculate', 'manage', 'cancel', 'delete', 'archive', 'reopen', 'publish', 'send', 'notify'],
                scope: 'assigned-leagues',
            },
            {
                // Read-only access to players and staff to link disciplinary cases.
                resources: ['player', 'players', 'staff', 'team', 'teams'],
                actions: READ_ACTIONS,
                scope: 'assigned-leagues',
            },
            {
                resources: ['disciplinary'],
                actions: ['view'],
                scope: 'assigned-leagues',
            },
        ],
    },
    [UserRole.DISCIPLINARY_SECRETARY]: {
        key: UserRole.DISCIPLINARY_SECRETARY,
        label: 'Secrétaire de Commission',
        description: 'Organise les séances, convoque les parties et publie les procès-verbaux (PV).',
        grants: [
            {
                resources: ['disciplinary_case', 'sanction', 'sanctions', 'suspension', 'suspensions', 'disciplinary_accumulation_rule'],
                actions: READ_ACTIONS,
                scope: 'assigned-leagues',
            },
            {
                resources: ['disciplinary_session', 'document'],
                actions: [...READ_ACTIONS, 'create', 'update', 'send', 'publish', 'notify'],
                scope: 'assigned-leagues',
            },
            {
                // Read-only access to players and staff for session organization.
                resources: ['player', 'players', 'staff', 'team', 'teams'],
                actions: READ_ACTIONS,
                scope: 'assigned-leagues',
            },
        ],
    },

    // --- External & Limited Roles ---

    [UserRole.OFFICIAL]: {
        key: UserRole.OFFICIAL,
        label: 'Officiel',
        description: 'Consulte ses désignations, gère ses indisponibilités et soumet ses notes de frais.',
        grants: [
            {
                // Can see assignments in leagues they are eligible for.
                resources: ['dashboard', 'match', 'matches', 'assignment', 'assignments', 'match_sheet'],
                actions: READ_ACTIONS,
                scope: 'assigned-leagues',
            },
            {
                // Can only manage their own profile, availability, and expenses.
                resources: ['official', 'availability', 'availabilities', 'expense_report'],
                actions: [...READ_ACTIONS, 'create', 'update', 'submit'],
                scope: 'own',
            },
            {
                // Can view their own communications.
                resources: ['notification', 'notifications', 'email', 'emails'],
                actions: READ_ACTIONS,
                scope: 'own',
            },
        ],
    },
    [UserRole.REFREE_MANAGER]: {
        key: UserRole.REFREE_MANAGER,
        label: 'Responsable Arbitres',
        description: 'Consulte les informations des arbitres (hors coordonnées bancaires) et leurs désignations, et saisit les désignations d’arbitres de la commission d’arbitrage pour les matchs à venir ; la ligue valide.',
        grants: [
            {
                // Read-only access to officials information (excluding bank account details - to be filtered at UI/service level).
                // NOTE: Bank account exclusion must be enforced at the service/UI layer.
                resources: ['official', 'officials'],
                actions: READ_ACTIONS,
                scope: 'assigned-leagues',
            },
            {
                // Read-only access to assignments for referees only (filtering by official_type='referee' required at service/query level).
                // NOTE: Type-based filtering (referee vs delegate) must be enforced at the service/query layer.
                resources: ['assignment', 'assignments'],
                actions: READ_ACTIONS,
                scope: 'assigned-leagues',
            },
            {
                // Read-only access to matches to provide context for assignments.
                resources: ['dashboard', 'match', 'matches'],
                actions: READ_ACTIONS,
                scope: 'assigned-leagues',
            },
            {
                // Propose les arbitres (créneaux d'arbitre uniquement) ; jamais de
                // désignation directe : la ligue valide (RPC propose_referee / close_referee_proposal).
                resources: ['assignment_proposal'],
                actions: [...READ_ACTIONS, 'create', 'update', 'withdraw'],
                scope: 'assigned-leagues',
            },
        ],
    },
    [UserRole.TEAM_MANAGER]: {
        key: UserRole.TEAM_MANAGER,
        label: 'Responsable d’Équipe',
        description: 'Suit les rencontres de son équipe et consulte les communications officielles.',
        grants: [
            {
                resources: ['dashboard', 'match', 'matches', ...COMMUNICATION_RESOURCES],
                actions: [...READ_ACTIONS, 'comment'],
                scope: 'assigned-teams',
            },
        ],
    },
    [UserRole.SECURITY_GATE]: {
        key: UserRole.SECURITY_GATE,
        label: 'Contrôle d’Accès Stade',
        description: 'Scanne les badges média à l’entrée du stade et consulte le journal des accès de ses ligues.',
        grants: [
            {
                // The operator scans in the webapp scan mode (/media/scan) or the
                // mobile app, reaches the module and reads back the log their
                // scans produce. The DB predicate is_media_gate_scanner() is what
                // authorises the write, and it deliberately excludes reviewers —
                // this grant is the mirror image: no 'approve'/'reject' anywhere.
                // 'media' is the module gate only, hence 'view' alone — same
                // shape as it has for the league president.
                resources: ['media'],
                actions: ['view'],
                scope: 'assigned-leagues',
            },
            {
                // 'scan' = scanning at the gate (/media/scan). It mirrors the
                // server's MEDIA_GATE_SCANNER_ROLES (super_admin, security_gate,
                // league_admin, media_manager — the last two do not exist in
                // this webapp): only those get an ENTRY recorded and the full
                // scan payload. The league president reads the log but is
                // deliberately NOT a scanner, so it never gets 'create' here —
                // a scan-mode button for them would record mere verifications.
                resources: ['media_access_log'],
                actions: [...READ_ACTIONS, 'scan'],
                scope: 'assigned-leagues',
            },
            {
                // Reading an accreditation is part of resolving a disputed scan
                // ("this badge says pitchside, the app refused it"). Read-only:
                // a turnstile operator never changes a decision.
                resources: ['media_accreditation'],
                actions: ['view', 'list'],
                scope: 'assigned-leagues',
            },
            {
                // Matches give the log its context (which fixture, which stadium).
                // 'match' has no 'list' in the catalog (it is the singular
                // record); the plural resource carries the listing action.
                resources: ['matches'],
                actions: ['view', 'list'],
                scope: 'assigned-leagues',
            },
            {
                resources: ['match'],
                actions: ['view'],
                scope: 'assigned-leagues',
            },
        ],
    },
    [UserRole.VIEWER]: {
        key: UserRole.VIEWER,
        label: 'Visiteur',
        description: 'Accès public en lecture seule sur les données non-sensibles comme les calendriers et classements.',
        grants: [
            {
                resources: ['matches', 'officials', 'clubs', 'teams', 'rankings', 'stadiums', 'venues'],
                actions: ['view', 'list'],
                scope: 'global',
            },
        ],
    },
};